/**
 * 機材プリセット管理
 *
 * Phase 2 では localStorage で実装。
 * Phase 9 で IndexedDB に移行する予定（store.js を介して透過的に置換）。
 */

import { FILMS, getFilmById } from './data/films.js';
import { DEVELOPERS } from './data/developers.js';

const STORAGE_KEY = 'zn.presets.v1';
const ACTIVE_KEY = 'zn.presets.active';

/**
 * プリセットの形
 *
 * GearPreset {
 *   id: string,
 *   name: string,
 *   isFavorite: boolean,
 *   createdAt: number,
 *   updatedAt: number,
 *
 *   camera: string,
 *   filmFormat: '645' | '66' | '67' | '68' | '69' | '617',
 *   defaultOrientation: 'auto' | 'portrait' | 'landscape',
 *   defaultLens: string,
 *
 *   filmId: string,
 *   ei: number,
 *
 *   filterId: '' | 'yellow8' | 'orange16' | 'red25' | 'green11' | 'polarizer',
 *
 *   defaultDeveloperId: string,
 *
 *   defaultShadowZone: number, // 0-10
 *   defaultHighlightZone: number,
 *
 *   personalDevTimeOffset?: number  // ±秒、N時間に対する個人補正
 * }
 */

export const FORMAT_RATIOS = {
  '645': [4.5, 6],   // 645 は基本横長
  '66':  [1, 1],
  '67':  [6, 7],
  '68':  [6, 8],     // 横長基本
  '69':  [6, 9],
  '617': [6, 17],
};

export const FILTER_FACTORS = {
  '':           { name: 'none',          stops: 0,    monoEffect: 'none' },
  'yellow8':    { name: 'Yellow #8 (Y2)',stops: 0.7,  monoEffect: 'yellow' },
  'orange16':   { name: 'Orange #16',    stops: 1.5,  monoEffect: 'orange' },
  'red25':      { name: 'Red #25 (R60)', stops: 3.0,  monoEffect: 'red' },
  'green11':    { name: 'Green #11',     stops: 1.5,  monoEffect: 'green' },
  'polarizer':  { name: 'Polarizer',     stops: 1.5,  monoEffect: 'polarizer' },
};

function loadAll() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    console.warn('[presets] load failed:', e);
    return [];
  }
}

function saveAll(arr) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(arr));
}

export function listPresets() {
  return loadAll().sort((a, b) => {
    if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
    return (b.updatedAt || 0) - (a.updatedAt || 0);
  });
}

export function getPreset(id) {
  return loadAll().find((p) => p.id === id);
}

export function createPreset(data) {
  const now = Date.now();
  const preset = {
    id: `p_${now.toString(36)}_${Math.random().toString(36).slice(2, 7)}`,
    name: 'New Preset',
    isFavorite: false,
    createdAt: now,
    updatedAt: now,
    camera: '',
    filmFormat: '67',
    defaultOrientation: 'auto',
    defaultLens: '',
    defaultLensFocalMm: null,   // mm 単位、null なら未指定（ズーム適用しない）
    filmId: 'tri-x-400',
    ei: 400,
    filterId: '',
    defaultDeveloperId: 'd76-1+1',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
    personalDevTimeOffset: 0,
    ...data,
  };
  const all = loadAll();
  all.push(preset);
  saveAll(all);
  return preset;
}

/** 複製（6）：お気に入りは外し、名前に「のコピー」を付ける */
export function duplicatePreset(id) {
  const src = getPreset(id);
  if (!src) return null;
  const { id: _id, createdAt, updatedAt, ...rest } = src;
  return createPreset({ ...rest, name: `${src.name} のコピー`, isFavorite: false });
}

export function updatePreset(id, patch) {
  const all = loadAll();
  const idx = all.findIndex((p) => p.id === id);
  if (idx === -1) return null;
  all[idx] = { ...all[idx], ...patch, id, updatedAt: Date.now() };
  saveAll(all);
  return all[idx];
}

export function deletePreset(id) {
  const all = loadAll().filter((p) => p.id !== id);
  saveAll(all);
  if (getActivePresetId() === id) {
    setActivePresetId(null);
  }
}

export function toggleFavorite(id) {
  const p = getPreset(id);
  if (!p) return null;
  return updatePreset(id, { isFavorite: !p.isFavorite });
}

export function getActivePresetId() {
  return localStorage.getItem(ACTIVE_KEY) || null;
}

export function setActivePresetId(id) {
  if (id) localStorage.setItem(ACTIVE_KEY, id);
  else localStorage.removeItem(ACTIVE_KEY);
}

/**
 * プリセットの表示用ラベル（ステータスバー等で使用）
 */
export function formatPresetSummary(preset) {
  if (!preset) return '—';
  const film = getFilmById(preset.filmId);
  const filmName = film ? film.name : preset.filmId;
  const filter = FILTER_FACTORS[preset.filterId];
  const parts = [
    preset.camera || 'Camera',
    filmName,
    `EI ${preset.ei}`,
  ];
  if (filter && filter.name !== 'none') parts.push(filter.name.split(' ')[0]);
  return parts.join(' · ');
}

export function formatPresetFormat(preset) {
  if (!preset) return '';
  return preset.filmFormat;
}

/**
 * 選択フォーマットのアスペクト比 [w, h]
 * orientation を考慮（portrait なら必要に応じて入れ替え）
 */
export function getAspectRatio(preset, orientation) {
  if (!preset) return [4, 3];
  const ratio = FORMAT_RATIOS[preset.filmFormat] || [4, 3];
  const orient = orientation || preset.defaultOrientation || 'auto';
  if (orient === 'landscape') {
    return [Math.max(ratio[0], ratio[1]), Math.min(ratio[0], ratio[1])];
  }
  if (orient === 'portrait') {
    return [Math.min(ratio[0], ratio[1]), Math.max(ratio[0], ratio[1])];
  }
  // auto: そのまま
  return ratio;
}

/**
 * 初回起動時のサンプルプリセットを生成（プリセットが0件の場合のみ）
 */
export function seedDefaultPresetsIfEmpty() {
  if (loadAll().length > 0) return;
  createPreset({
    name: 'Pentax 67 + Tri-X 400',
    isFavorite: true,
    camera: 'Pentax 67',
    filmFormat: '67',
    defaultOrientation: 'auto',
    defaultLens: 'SMC Takumar 105/2.4',
    defaultLensFocalMm: 105,
    filmId: 'tri-x-400',
    ei: 320,
    filterId: 'yellow8',
    defaultDeveloperId: 'd76-1+1',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
  });
  createPreset({
    name: 'Hasselblad 500CM + HP5 Plus',
    camera: 'Hasselblad 500CM',
    filmFormat: '66',
    defaultOrientation: 'auto',
    defaultLens: 'Zeiss Planar 80/2.8',
    defaultLensFocalMm: 80,
    filmId: 'hp5-plus',
    ei: 400,
    filterId: '',
    defaultDeveloperId: 'id11-1+1',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
  });
  createPreset({
    name: 'Mamiya 645 + Acros 100 II',
    camera: 'Mamiya 645',
    filmFormat: '645',
    defaultOrientation: 'landscape',
    defaultLens: 'Sekor 80/2.8',
    defaultLensFocalMm: 80,
    filmId: 'acros-100-ii',
    ei: 100,
    filterId: 'yellow8',
    defaultDeveloperId: 'xtol-1+1',
    defaultShadowZone: 3,
    defaultHighlightZone: 8,
  });
}
